import { action, makeObservable, observable } from 'mobx';

import type { OrderData, PaymentData, PaymentParameterData, ShoppingContext } from '../../../data/model';
import { CheckoutPageTranslation } from '../../../localization/SiteTranslation';
import { AppError } from '../../../shared/AppError';
import Logger from '../../../shared/Logger';
import { AsyncScriptLoader } from '../AsyncScriptLoader';
import { AuthorizationCompletedEventArgs, BasePaymentMethod } from '../IPaymentMethod';
import { PaymentParameterDataExtensions } from '../PaymentParameterDataExtensions';

export interface KlarnaAssetUrls {
  standard: string;
  descriptive: string;
}

export interface KlarnaPaymentMethodCategory {
  identifier: string;
  name: string;
  asset_urls: KlarnaAssetUrls;
}

export class KlarnaPaymentMethod extends BasePaymentMethod {
  private translation: CheckoutPageTranslation;
  private order: OrderData;
  private payment: PaymentData;

  @observable
  public categories: KlarnaPaymentMethodCategory[] = [];
  public selectedCategory?: KlarnaPaymentMethodCategory;

  @action
  public setSelectedCategory(category: KlarnaPaymentMethodCategory) {
    this.selectedCategory = category;
  }

  constructor(translation: CheckoutPageTranslation) {
    super('KlarnaPayments');
    makeObservable(this);
    this.translation = translation;
    this.showAuthorizeButton = true;

    this.ensureScriptLoaded();
  }

  public getInitialParameters(shoppingContext: ShoppingContext): Array<PaymentParameterData> {
    return [
      {
        key: 'Locale',
        value: shoppingContext.languageCode,
      },
    ];
  }

  private async ensureScriptLoaded(): Promise<void> {
    return AsyncScriptLoader.ensureLoaded('https://x.klarnacdn.net/kp/lib/v1/api.js', 'anonymous');
  }

  @action
  async initialize(order: OrderData, payment: PaymentData): Promise<void> {
    this.setInitialized(false);

    this.order = order;
    this.payment = payment;

    this.categories = this.getPaymentMethodCategories();
    this.selectedCategory = this.categories.first();

    await this.ensureScriptLoaded();

    Klarna.Payments.init({ client_token: this.getClientToken() });
  }

  onWidgetRendered() {
    try {
      Klarna.Payments.load(
        {
          container: '#klarna_container',
          payment_method_categories: this.categories.map((x) => x.identifier),
          instance_id: 'klarna-widget',
        },
        (response) => {
          this.setInitialized(true);
          if (!response.show_form) {
            Logger.exception('Klarna widget initialization failed', new Error(JSON.stringify(response.error ?? {})));
          }
        },
      );
    } catch (e) {
      this.setInitialized(true);
      Logger.exception('Klarna widget initialization failed', e);
    }
  }

  public authorize(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      Klarna.Payments.authorize(
        {
          payment_method_category: this.selectedCategory?.identifier,
          auto_finalize: true,
        },
        {},
        (result) => {
          if (result.approved) {
            if (result.finalize_required) {
              Logger.error(
                'Authorization ended with `finalization_required` flag set to true. This is unexpected as `auto_finalize` was set to true',
              );
              reject();
              return;
            }
            const args: AuthorizationCompletedEventArgs = {
              parameters: [
                { key: 'authorization_token', value: result.authorization_token },
                { key: 'session_id', value: this.getSessionId() },
              ],
            };
            this.authorizationCompleted.raise(args);
            resolve();
          } else if (result.show_form) {
            // Authorization not approved but keep showing the form
            resolve();
          } else {
            const error = new AppError(
              this.translation.errors.authorizingPaymentFailed,
              `Payment authorization failed, order id = ${this.order.id}, error = ${JSON.stringify(result.error)}}`,
            );
            reject(error);
          }
        },
      );
    });
  }

  private getSessionId() {
    return PaymentParameterDataExtensions.GetRequired(this.payment.parameters, 'session_id');
  }

  private getClientToken() {
    return PaymentParameterDataExtensions.GetRequired(this.payment.parameters, 'client_token');
  }

  private getPaymentMethodCategories() {
    const categoriesJson = PaymentParameterDataExtensions.GetRequired(
      this.payment.parameters,
      'payment_method_categories',
    );
    return (JSON.parse(categoriesJson) as KlarnaPaymentMethodCategory[]) ?? [];
  }

  @action
  private setInitialized(value: boolean) {
    this.isInitialized = value;
  }
}
